/**
 * Vendor detail body — mirrors the prototype main content area.
 *
 * Structure follows `prototype/purchases/vendor-detail.html`: body grid, Recent bills
 * panel with awaiting callout, grouped Activity timeline, then the sidebar
 * cards for delivery reliability, spend trend, pay-to bank, pinned note, and
 * quick actions.
 *
 * @see prototype/purchases/vendor-detail.html `.body-grid`
 */
import { Link } from "@tanstack/react-router";
import { Check, Clock3, Download, FileText, MessageCircle, Pencil, TrendingUp } from "lucide-react";
import type {
  VendorBillRow,
  VendorDetailModel,
  VendorDetailRow,
  VendorTimelineTone,
} from "../lib/detail-model.ts";

export interface VendorDetailOverviewProps {
  vendor: VendorDetailRow;
  model: VendorDetailModel;
  coolingOff: {
    inCoolingOff: boolean;
    availableAt: Date | null;
  };
  /** Optional — when provided, the Pay-to-bank card's Edit button calls
   *  this to open the change-bank-details dialog. Wires to
   *  `vendors.changeBankDetails` per ADR-0008 (step-up + 24h cooling-off). */
  onChangeBank?: (() => void) | undefined;
}

export function VendorDetailOverview({
  vendor,
  model,
  coolingOff,
  onChangeBank,
}: VendorDetailOverviewProps) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="space-y-4 xl:col-span-2">
        <RecentBillsPanel model={model} />
        <ActivityPanel model={model} />
      </div>
      <aside className="space-y-4" aria-label="Vendor sidebar">
        <ReliabilityCard model={model} />
        <SpendTrendCard model={model} />
        <PayToBankCard model={model} coolingOff={coolingOff} onChangeBank={onChangeBank} />
        {model.pinnedNote ? <PinnedNote note={model.pinnedNote} /> : null}
        <QuickActions vendor={vendor} model={model} />
      </aside>
    </div>
  );
}

function RecentBillsPanel({ model }: { model: VendorDetailModel }) {
  return (
    <section
      className="overflow-hidden rounded-md border border-line bg-paper shadow-sm"
      aria-labelledby="vendor-bills-head"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <h3 id="vendor-bills-head" className="text-lg font-semibold text-ink">
            Recent bills
          </h3>
          <p className="text-sm text-muted">{model.billsSummary}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-8 items-center gap-1.5 rounded-sm px-3 text-xs font-semibold text-ink hover:bg-bg"
          >
            <Download size={13} aria-hidden />
            Export
          </button>
          <Link
            to="/procurement/bills"
            className="inline-flex h-8 items-center rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink hover:bg-bg"
          >
            View all
          </Link>
        </div>
      </header>

      {model.awaitingBill ? (
        <div className="m-5 grid gap-3 rounded-md border border-warn-100 bg-warn-50 p-4 md:grid-cols-[1fr_auto]">
          <div>
            <p className="font-semibold text-ink">
              {model.awaitingBill.id} · {model.awaitingBill.amount} awaiting action
            </p>
            <p className="mt-1 text-sm text-muted">{model.awaitingBill.meta}</p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Link
              to="/procurement/bills"
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-warn-100 bg-paper px-3 text-sm font-semibold text-ink hover:bg-warn-50"
            >
              <FileText size={14} aria-hidden />
              Open
            </Link>
            <button
              type="button"
              disabled
              className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-sm bg-ink px-3 text-sm font-semibold text-white opacity-80"
            >
              <Check size={14} aria-hidden />
              Approve & pay
            </button>
          </div>
        </div>
      ) : null}

      {model.bills.length ? (
        <ul className="divide-y divide-line">
          {model.bills.map((bill) => (
            <BillListItem key={bill.id} bill={bill} />
          ))}
        </ul>
      ) : (
        <div className="border-t border-line px-5 py-6 text-sm text-muted">
          No bills found in the database for this vendor yet.
        </div>
      )}
    </section>
  );
}

export function VendorBillsPanel({ model }: { model: VendorDetailModel }) {
  return <RecentBillsPanel model={model} />;
}

function BillListItem({ bill }: { bill: VendorBillRow }) {
  const awaiting = bill.tone === "awaiting" || bill.tone === "review";
  const paid = bill.tone === "paid";
  return (
    <li className="grid grid-cols-[2rem_minmax(0,1fr)_auto_auto] items-center gap-3 px-5 py-3 hover:bg-raised">
      <span
        className={`grid h-8 w-8 place-items-center rounded-full ${awaiting ? "bg-warn-50 text-warn" : paid ? "bg-ok-50 text-ok" : "bg-raised text-muted"}`}
        aria-hidden
      >
        {awaiting ? <Clock3 size={14} /> : paid ? <Check size={14} /> : <FileText size={14} />}
      </span>
      <div className="min-w-0">
        <p className="font-mono text-sm font-semibold text-ink">{bill.displayNumber}</p>
        <p className="truncate text-xs text-muted">
          {bill.date} · {bill.description} · {bill.meta}
        </p>
      </div>
      <span
        className={`font-mono text-sm font-semibold ${awaiting ? "text-warn" : paid ? "text-ok" : "text-ink"}`}
      >
        {bill.amount}
      </span>
      <span
        className={`rounded-full border px-2 py-1 text-xs font-semibold ${awaiting ? "border-warn-100 bg-warn-50 text-warn" : paid ? "border-ok-100 bg-ok-50 text-ok" : "border-line bg-raised text-muted"}`}
      >
        {bill.statusLabel}
      </span>
    </li>
  );
}

function ActivityPanel({ model }: { model: VendorDetailModel }) {
  return (
    <section
      className="overflow-hidden rounded-md border border-line bg-paper shadow-sm"
      aria-labelledby="vendor-activity-head"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <h3 id="vendor-activity-head" className="text-lg font-semibold text-ink">
            Activity
          </h3>
          <p className="text-sm text-muted">
            All vendor events · auto-captured from bills, GRNs & bank feed
          </p>
        </div>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 rounded-sm px-3 text-xs font-semibold text-ink hover:bg-bg"
        >
          <TrendingUp size={13} aria-hidden />
          Filter
        </button>
      </header>

      <ol className="px-5 pb-4">
        {model.activity.map((group) => (
          <li key={group.label} className="list-none">
            <div className="py-4 pl-11 text-xs font-semibold uppercase tracking-wider text-muted">
              {group.label}
            </div>
            <ol className="space-y-0">
              {group.items.map((item, index) => (
                <li key={item.id} className="relative flex gap-3 pb-4">
                  {index < group.items.length - 1 ? (
                    <span aria-hidden className="absolute left-4 top-8 h-full w-px bg-line" />
                  ) : null}
                  <span
                    className={`z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border ${timelineTone(item.tone)}`}
                    aria-hidden
                  >
                    {timelineIcon(item.tone)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-semibold text-ink">{item.title}</p>
                      <span className="font-mono text-xs text-muted">{item.when}</span>
                    </div>
                    <p className="mt-1 text-sm leading-6 text-muted">
                      {item.body}
                      {item.linkLabel ? (
                        <Link
                          to="/procurement/bills"
                          className="ml-2 font-semibold text-sky-700 hover:underline"
                        >
                          {item.linkLabel}
                        </Link>
                      ) : null}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ReliabilityCard({ model }: { model: VendorDetailModel }) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h4 className="text-sm font-semibold text-ink">Delivery reliability</h4>
        <span className="text-xs font-bold text-ok">{model.reliability.percent}</span>
      </header>
      <div className="space-y-3 p-4">
        {model.reliability.rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-3 text-sm">
            <span className="text-muted">{row.label}</span>
            <span className={row.tone === "ok" ? "font-semibold text-ok" : "font-mono text-ink"}>
              {row.value}
            </span>
          </div>
        ))}
        <div className="flex gap-1.5" role="img" aria-label="Delivery reliability history">
          {model.reliability.dots.map((dot, index) => (
            <span
              // Static sparkline positions, order is the semantic identity.
              key={`${dot}-${index}`}
              title={dot === "empty" ? "No GRN data" : dot === "now" ? "Current period" : "On-time"}
              className={`h-5 flex-1 rounded-sm ${dot === "empty" ? "border border-line bg-paper" : dot === "now" ? "border border-ink bg-paper" : "bg-ok"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function SpendTrendCard({ model }: { model: VendorDetailModel }) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h4 className="text-sm font-semibold text-ink">Spend trend · 6 mo</h4>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-semibold text-muted hover:bg-bg hover:text-ink"
        >
          <TrendingUp size={12} aria-hidden />
          Detail
        </button>
      </header>
      <div className="p-4">
        <div
          className="flex h-32 items-end gap-3 border-b border-line pb-3"
          role="img"
          aria-label="Monthly spend over last 6 months"
        >
          {model.spendTrend.months.map((month) => (
            <div key={month.label} className="flex flex-1 flex-col items-center justify-end gap-2">
              <span
                className={`w-full rounded-t-sm ${month.tone === "now" ? "bg-ink" : "bg-sky-600"} ${month.heightClass}`}
              />
              <span className="text-xs text-muted">{month.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-between text-sm">
          <span className="text-muted">YTD spend</span>
          <span className="font-mono font-semibold text-ink">{model.spendTrend.ytd}</span>
        </div>
        <div className="mt-2 flex justify-between text-sm">
          <span className="text-muted">Last 12 mo</span>
          <span className="font-mono font-semibold text-ink">{model.spendTrend.last12Months}</span>
        </div>
      </div>
    </section>
  );
}

function PayToBankCard({
  model,
  coolingOff,
  onChangeBank,
}: {
  model: VendorDetailModel;
  coolingOff: VendorDetailOverviewProps["coolingOff"];
  onChangeBank?: (() => void) | undefined;
}) {
  const disabled = coolingOff.inCoolingOff || !onChangeBank;
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h4 className="text-sm font-semibold text-ink">Pay-to bank</h4>
        <button
          type="button"
          disabled={disabled}
          onClick={onChangeBank}
          title={
            coolingOff.inCoolingOff
              ? "Cooling-off active — wait until the 24-hour window ends."
              : "Change account number + IFSC. Requires Founder OTP + 24-hour cooling-off (ADR-0008)."
          }
          className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-semibold ${
            disabled
              ? "cursor-not-allowed text-muted opacity-70"
              : "text-ink-2 hover:bg-bg cursor-pointer"
          }`}
        >
          <Pencil size={12} aria-hidden />
          {coolingOff.inCoolingOff ? "Locked" : "Change →"}
        </button>
      </header>
      <div className="space-y-2 p-4 text-sm">
        <BankLine label="Account name" value={model.bank.accountName} />
        <BankLine label="Bank" value={model.bank.bankName} />
        <BankLine label="Account #" value={model.bank.maskedAccount} mono />
        <BankLine label="IFSC" value={model.bank.ifsc} mono />
        <p className="flex items-center gap-1 text-xs font-semibold text-ok">
          <Check size={12} aria-hidden />
          {coolingOff.inCoolingOff && coolingOff.availableAt
            ? `Cooling-off until ${coolingOff.availableAt.toLocaleString("en-IN")}`
            : model.bank.verification}
        </p>
      </div>
    </section>
  );
}

function BankLine({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted">{label}</span>
      <span className={`text-right text-ink ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function PinnedNote({ note }: { note: NonNullable<VendorDetailModel["pinnedNote"]> }) {
  return (
    <div className="rounded-md border border-sky-100 bg-sky-50 p-4 text-sm text-sky-900">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-sky-700">
        {note.when}
      </span>
      {note.body}
    </div>
  );
}

function QuickActions({ vendor, model }: { vendor: VendorDetailRow; model: VendorDetailModel }) {
  const contactName = vendor.contactName || "vendor";
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="border-b border-line px-4 py-3">
        <h4 className="text-sm font-semibold text-ink">Quick actions</h4>
      </header>
      <div className="space-y-2 p-3">
        {model.awaitingBill ? (
          <QuickAction primary icon={<Check size={14} aria-hidden />}>
            Approve & pay {model.awaitingBill.id}
          </QuickAction>
        ) : null}
        <QuickAction icon={<FileText size={14} aria-hidden />}>Raise PO</QuickAction>
        <QuickAction icon={<Download size={14} aria-hidden />}>Send statement</QuickAction>
        {vendor.phone ? (
          <a
            href={`tel:+91${vendor.phone}`}
            className="flex items-center gap-2 rounded-sm border border-line px-3 py-2 text-sm font-semibold text-ink hover:bg-bg"
          >
            <MessageCircle size={14} aria-hidden />
            Message {contactName}
          </a>
        ) : (
          <QuickAction icon={<MessageCircle size={14} aria-hidden />}>
            Message {contactName}
          </QuickAction>
        )}
        <Link
          to="/procurement/vendors/$vendorId/edit"
          params={{ vendorId: vendor.id }}
          className="flex items-center gap-2 rounded-sm border border-line px-3 py-2 text-sm font-semibold text-ink hover:bg-bg"
        >
          <Pencil size={14} aria-hidden />
          Edit vendor details
        </Link>
      </div>
    </section>
  );
}

function QuickAction({
  icon,
  children,
  primary,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      disabled
      className={`flex w-full cursor-not-allowed items-center gap-2 rounded-sm border px-3 py-2 text-left text-sm font-semibold opacity-80 ${
        primary ? "border-ink bg-ink text-white" : "border-line text-ink"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function timelineTone(tone: VendorTimelineTone): string {
  switch (tone) {
    case "await":
      return "border-warn bg-warn-50 text-warn";
    case "match":
    case "paid":
      return "border-ok bg-ok-50 text-ok";
    case "issued":
      return "border-sky-600 bg-sky-50 text-sky-700";
    case "note":
    default:
      return "border-ink bg-raised text-ink";
  }
}

function timelineIcon(tone: VendorTimelineTone) {
  switch (tone) {
    case "await":
      return <Clock3 size={13} />;
    case "match":
    case "paid":
      return <Check size={13} />;
    case "issued":
      return <FileText size={13} />;
    case "note":
    default:
      return <MessageCircle size={13} />;
  }
}
